<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { UiAsyncCombobox } from '@shopexperts/shared';
import { usePageSearch } from '../../composables/usePageSearch';

const props = defineProps<{
  name?: string;
  placeholder?: string;
}>();

const model = defineModel<string>({ default: '' });
const emit = defineEmits<{
  (event: 'select-page-label', label: string): void;
}>();

/** The input shows the stored URL; editing it doubles as the page search query. */
const searchQuery = ref(model.value);

watch(model, (next) => {
  if (next === searchQuery.value) return;
  // An external rewrite ends the pending selection (unless it re-asserts the
  // same URL) so the old label can no longer resolve to a stale URL.
  if (lastSelection.value && next !== lastSelection.value.url) lastSelection.value = null;
  searchQuery.value = next;
});

const { groupedData, isFetching, isError, error } = usePageSearch(searchQuery);
const searchError = computed(() => (error.value instanceof Error ? error.value : undefined));

/**
 * In mode="preserve" the combobox rewrites its input to the selected item's
 * label after emitting select — and again on every blur — and each echo
 * arrives here as a plain text edit; map it back to the selected URL or it
 * clobbers the stored href. Any other edit invalidates the mapping so typing
 * the literal label text later is kept as-is instead of the stale URL.
 */
const lastSelection = ref<{ label: string; url: string } | null>(null);

function commitText(next: string | undefined) {
  const raw = next ?? '';
  let value = raw;
  if (lastSelection.value) {
    if (raw === lastSelection.value.label) value = lastSelection.value.url;
    else if (raw !== lastSelection.value.url) lastSelection.value = null;
  }
  searchQuery.value = value;
  model.value = value;
}

function onSelect(selection: { value: string | string[]; label: string | string[] }) {
  const url = Array.isArray(selection.value) ? selection.value[0] : selection.value;
  const label = Array.isArray(selection.label) ? selection.label[0] : selection.label;
  if (url) {
    lastSelection.value = { label: label ?? '', url };
    commitText(url);
    emit('select-page-label', label ?? '');
  }
}
</script>

<template>
  <UiAsyncCombobox
    :model-value="searchQuery"
    :name="props.name"
    :data="groupedData"
    :is-fetching="isFetching"
    :is-error="isError"
    :error="searchError"
    size="md"
    display-mode="combobox"
    mode="preserve"
    hide-combobox-when-empty
    :placeholder="props.placeholder || 'Search pages, or type a URL…'"
    @update:model-value="commitText"
    @select="onSelect"
  />
</template>
